import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {map, switchMap, tap} from 'rxjs/operators';
import * as actions from 'sp-mark/redux/router/router.actions';
import {LocalUnit, Payment} from 'sp-ui-lib';

@Injectable()
export class RouterEffects {

  navigateTo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.navigateTo),
      tap(routingObject => this.router.navigateByUrl(routingObject.path)),
      switchMap(routingObject => [
        actions.activateMenuItem({menuItemId: routingObject.id}),
        actions.storeItemToNavigationLog({routingObject: {id: routingObject.id, path: routingObject.path}})
      ])
    )
  );

  basicNavigateTo$ = createEffect(() =>
      this.actions$
        .pipe(
          ofType(actions.basicNavigateTo),
          tap(routingObject => this.router.navigateByUrl(routingObject.path))
        ),
    {dispatch: false}
  );

  /* to LocalUnitDetail */
  navigateToLocalUnitDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.navigateToLocalUnitDetail),
      tap((prop: LocalUnit) =>
        this.router.navigateByUrl(`local-units/${prop.id}`)
      ),
      map(() => actions.activateMenuItem({menuItemId: 'local_units'}))
    )
  );

  navigateToEditUnit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.navigateToEditUnit),
      map(action => action.localUnit),
      tap((prop: LocalUnit) =>
        this.router.navigateByUrl(`local-units/edit-unit/${prop.id}`)
      ),
      map(() => actions.activateMenuItem({menuItemId: 'local_units'}))
    )
  );

  /* to Gateway */
  navigateToGateways$ = createEffect(() =>
      this.actions$.pipe(
        ofType(actions.navigateToGateways),
        tap(() => this.router.navigateByUrl('gateways'))
      ),
    {dispatch: false}
  );

  navigateToGateway$ = createEffect(() =>
      this.actions$.pipe(
        ofType(actions.navigateToGateway),
        tap(action => this.router.navigateByUrl(`gateways/${action.serviceId}`))
      ),
    {dispatch: false}
  );

  navigateToAddGateway$ = createEffect(() =>
      this.actions$.pipe(
        ofType(actions.navigateToAddGateway),
        tap(() => this.router.navigateByUrl(`gateways/add`))
      ),
    {dispatch: false}
  );

  /* to Config Physical pos */
  navigateToConfigPos$ = createEffect(() =>
      this.actions$.pipe(
        ofType(actions.navigateToConfigurePos),
        tap(action => this.router.navigateByUrl(`local-units/configure-pos/${action.idLocalUnit}`))
      ),
    {dispatch: false}
  );

  /* to UserDetail */
  navigateToUserDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.navigateToUserDetail),
      map(action => action.user),
      tap(user => this.router.navigateByUrl(`users/${user.id}`)),
      map(() => actions.activateMenuItem({menuItemId: 'users'}))
    )
  );

  navigateToEditUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.navigateToEditUser),
      map(action => action.user),
      tap(user => this.router.navigateByUrl(`users/create-user/${user.id}`)),
      map(() => actions.activateMenuItem({menuItemId: 'users'}))
    )
  );

  /* to CustomerDetail */
  navigateToCustomerDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.navigateToCustomerDetail),
      map(action => action.id),
      tap(id => this.router.navigateByUrl(`customers/${id}`)),
      map(() => actions.activateMenuItem({menuItemId: 'customers'}))
    )
  );
  navigateToContractualizeCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.navigateToContractualizeCustomer),
      map(action => action.id),
      tap(id => this.router.navigateByUrl(`customers/contractualize-transaction/${id}`)),
      map(() => actions.activateMenuItem({menuItemId: 'customers'}))
    )
  );

  /* to EditCustomerPaymentNote */
  navigateToEditCustomerPaymentNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.navigateToEditCustomerPaymentNote),
      map(action => action.id),
      tap(id => this.router.navigateByUrl(`customers/payment/edit/${id}`)),
      map(() => actions.activateMenuItem({menuItemId: 'customers'}))
    )
  );

  /* to TicketDetail */
  navigateToTicketDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.navigateToTicketDetail),
      map(action => action.ticket),
      tap(ticket => this.router.navigateByUrl(`request-support/${ticket.id}`)),
      map(() => actions.activateMenuItem({menuItemId: 'request_support'}))
    )
  );

  /* to ServiceDetail */
  navigateToServiceDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.navigateToServiceDetail),
      map(action => action.service),
      tap(service => this.router.navigateByUrl(`services/${service.id}`)),
      map(() => actions.activateMenuItem({menuItemId: 'services'}))
    )
  );

  /* to EditProduct */
  navigateToEditProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.navigateToEditProduct),
      map(action => action.productId),
      tap(productId => this.router.navigateByUrl(`products-catalog/edit-product/${productId}`)),
      map(() => actions.activateMenuItem({menuItemId: 'products_catalog'}))
    )
  );

  /* to serviceGraph */
  navigateToServiceGraph$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.navigateToServiceGraph),
      map(action => action.service),
      tap(service => {
        const serviceId: string = service ? String(service.id) : 'aggregato';
        this.router.navigateByUrl(`statistics/${serviceId}`);
      }),
      map(() => actions.activateMenuItem({menuItemId: 'statistics'}))
    )
  );

  /* to ServiceDetail */
  navigateToAssociateService$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.navigateToAssociateService),
      map(action => action.service),
      tap(service => this.router.navigateByUrl(`services/associate/${service.id}`)),
      map(() => actions.activateMenuItem({menuItemId: 'services'}))
    )
  );

  /* to ServiceDetail */
  navigateToEditService$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.navigateToEditService),
      map(action => action.service),
      tap(service => this.router.navigateByUrl(`services/edit/${service.id}`)),
      map(() => actions.activateMenuItem({menuItemId: 'services'}))
    )
  );

  /* to Receipt */
  navigateToReceiptDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.navigateToReceiptDetail),
      map(action => action.receipt),
      tap(service => this.router.navigateByUrl(`receipts/${service.id}`)),
      map(() => actions.activateMenuItem({menuItemId: 'receipts'}))
    )
  );

  /* to Pagamenti */
  navigateToPaymentsDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.navigateToPaymentsDetail),
      tap((prop: Payment) => this.router.navigateByUrl(`/payments/${prop.id}`)),
      map(() => actions.activateMenuItem({menuItemId: 'payments'}))
    )
  );

  /* to Fee Details */
  navigateToInternalFeeDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.navigateToInternalFeeDetail),
      tap(action => this.router.navigateByUrl(`/payments/internal-fees/${action.rataSmallpayId}`)),
      map(() => actions.activateMenuItem({menuItemId: 'payments'}))
    )
  );

  navigateToExternalFeeDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.navigateToExternalFeeDetail),
      tap(action => this.router.navigateByUrl(`/payments/external-fees/${action.rataSmallpayId}`)),
      map(() => actions.activateMenuItem({menuItemId: 'payments'}))
    )
  );

  /* to Sell Preview */
  navigateToSellPreview$ = createEffect(() =>
      this.actions$.pipe(
        ofType(actions.navigateToSellPreview),
        tap(action => this.router.navigateByUrl(`/sell-preview/${action.idOperation}`))
      ),
    {dispatch: false}
  );

  /* to Sell Preview */
  navigateToConcludeSelling$ = createEffect(() =>
      this.actions$.pipe(
        ofType(actions.navigateToConcludeSelling),
        tap(action => this.router.navigateByUrl(`/conclude-sell/${action.idOperation}`, {replaceUrl: true}))
      ),
    {dispatch: false}
  );

  constructor(private actions$: Actions, private router: Router) {
  }
}
